import React, { useState } from 'react';
import { useParams } from 'react-router-dom';

import styled from 'styled-components';

import { useGetCreatorContents } from 'hooks/queries/creator';
import useCallbackRef from 'hooks/useCallbackRef';
import useCarousel from 'hooks/useCarousel';
import CoreLoading from 'shared/CoreLoading';
import {
	ReportSection,
	ReportSectionCard,
	ReportSectionHeader,
} from 'styles/styled-components/report';
import style from 'styles/styled-components/style';

import ContentCard from './content/ContentCard';
import ContentReport from './content/ContentReport';

const ContentsData = () => {
	const { groupId, creatorPk } = useParams();
	const { data: creatorContents, isLoading: isLoadingCreatorContents } =
		useGetCreatorContents(creatorPk);
	const [content_idx, setContentIdx] = useState<number>(0);

	const data =
		creatorContents?.sort(
			(a, b) => new Date(b.missionDate).getTime() - new Date(a.missionDate).getTime(),
		) ?? [];

	// carousel
	const [carousel_wrapper_dom, carousel_wrapper_ref] = useCallbackRef([data]);
	const [carousel_body_dom, carousel_body_ref] = useCallbackRef([data]);
	const [control, drag_constraints, drag_condition] = useCarousel(
		carousel_wrapper_dom,
		carousel_body_dom,
		[data],
	);

	if (isLoadingCreatorContents)
		return (
			<ReportSection>
				<ReportSectionHeader>콘텐츠 데이터</ReportSectionHeader>
				<Text typo="p" _margin="0 0 16px 0">
					인플루언서의 콘텐츠를 분석한 데이터입니다.
				</Text>

				<Grid flex="start" direction="column" _gap="16px">
					<Grid flex="start" _gap="16px" _overflow="hidden">
						{Array.from({ length: 4 }).map((_, idx) => (
							<ReportSectionCard
								animation={true}
								delay={`${idx * 0.5}s`}
								_width="270px"
								key={idx}
								shrink={true}
							>
								<Grid _height="398px" flex="center">
									<CoreLoading color_type="gray" />
								</Grid>
							</ReportSectionCard>
						))}
					</Grid>
					<Grid flex="start" _gap="16px">
						<ReportSectionCard animation={true} _width="max-content" _height="398px">
							<Grid _width="200px" _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
						<ReportSectionCard animation={true} delay="0.5s" _height="398px">
							<Grid _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
					</Grid>
					<Grid flex="start" _gap="16px">
						<ReportSectionCard
							animation={true}
							delay="0.5s"
							_width="max-content"
							_height="284px"
						>
							<Grid _width="200px" _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
						<ReportSectionCard animation={true} _height="284px">
							<Grid _height="100%" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
					</Grid>
					<Grid>
						<ReportSectionCard animation={true} delay="0.5s">
							<Grid _height="202px" flex="center">
								<CoreLoading color_type="gray" />
							</Grid>
						</ReportSectionCard>
					</Grid>
				</Grid>
			</ReportSection>
		);

	return (
		<ReportSection>
			<ReportSectionHeader>콘텐츠별 성과</ReportSectionHeader>
			<Text typo="p" _margin="0 0 16px 0">
				크리에이터가 미션 참여한 릴스 콘텐츠를 분석한 데이터입니다.
			</Text>

			<Grid flex="start" direction="column" _gap="16px">
				<ContentsCarouselWrapper ref={carousel_wrapper_ref} _margin="0 0 16px 0">
					<ContentsCarouselBody
						flex="start"
						_gap="16px"
						ref={carousel_body_ref}
						drag={data?.length < 4 ? false : 'x'}
						dragConstraints={drag_constraints}
						animate={control}
					>
						{data?.map((item, idx) => (
							<ContentCard
								key={idx}
								content={item}
								selected={content_idx === idx}
								_onClick={() => setContentIdx(idx)}
							/>
						))}
						{5 - (data?.length ?? 0) > 0 &&
							Array.from({ length: 5 - (data?.length ?? 0) }).map((_, idx) => (
								<SkeletonCard key={idx} />
							))}
					</ContentsCarouselBody>
				</ContentsCarouselWrapper>
				<ContentReport
					contentIdx={content_idx}
					contentPk={data?.[content_idx]?.contentPk}
				/>
			</Grid>
		</ReportSection>
	);
};

const { Grid, Text } = style;

const ContentsCarouselWrapper = styled(Grid)`
	overflow: hidden;
	max-width: 1080px;
`;

const ContentsCarouselBody = styled(Grid)`
	flex-wrap: nowrap;
`;

const SkeletonCard = styled(ReportSectionCard)`
	width: 270px;
	height: 453px;
	background-color: ${({ theme }) => theme.colors.uplus.bg10_lighten};

	flex-shrink: 0;
`;

export default ContentsData;
